import mapboxgl, { Marker, GeoJSONSource } from "mapbox-gl";
import {
  FilterSet,
  CustomMarker,
  SearchSet,
  RatingsBreakdown,
} from "../types/types";
import {
  defaultResponses,
  externalSources,
  mapHiddenWhereCategory,
  markerMapping,
  refugeRestroomsCategory,
  reviewsPageSize,
  runInDummyDataMode,
  writeToGA4,
  writeToLog,
} from "../const";
import { useCallback, useMemo } from "react";
import { areBoundsEqual } from "../functions/Functions";
import {
  countryCodeAllowList,
  primary1,
  white,
} from "@shane-collective/design-system/dist/const";
import { useAppState } from "../core/appState/UseAppStateZustand";
import {
  fetchCategoryForPoiCat,
  fetchLocation,
  fetchLocationRatingSummaries,
  getLocationReviews,
  listPoiCategoriesByCategory,
  voteOnReview,
} from "../client";
import { useMapFunctions } from "./UseMapFunctions";
import { Slide, toast } from "react-toastify";
import { dummyReviews } from "../dummy-data";
import { useShaneClientContext } from "../core/shaneClient/ShaneClientProvider";
import { usePopUp } from "./UsePopUp";
import { searchBoxSuggestionClickBlockedError } from "../strings";
import { debounce } from "lodash";
//@ts-ignore
import emoji from "emoji-dictionary";

interface UseSearchProps {
  isSmallScreen: boolean;
  popupContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  popup: mapboxgl.Popup;
  mapContainer: React.MutableRefObject<HTMLDivElement | null>;
  map: React.MutableRefObject<mapboxgl.Map | null>;
  namePopup: mapboxgl.Popup;
}

export function useSearch({
  isSmallScreen,
  map,
  popupContainerRef,
  popup,
  mapContainer,
  namePopup,
}: UseSearchProps) {
  const {
    showRatingMarkers,
    profile,
    resultsPosition,
    resultsPageNumber,
    setFilterSet,
    setIsSearching,
    setSearchSet,
    setSearchResults,
    setUnfilteredSearchResults,
    setIsLocationDialogOpen,
    setSelectedFeature,

    setLoadingMore,
    setIsNoResultsModalOpen,
    setIsSearchDialogOpen,
    setResultsPosition,
    setFakeSearchInputValue,
    setHideMap,
    setResultsPageNumber,
    setHasMoreResults,
    setLastSearchType,
    setInitialSearchViewportBounds,
    setDrawerContentLoaded,
    selectedFeature,
    databaseLocation,
    setDatabaseLocation,

    loadingMore,
    setNewMarker,
    newMarker,
    zoom,
    fakeSearchInputValue,
    allCertified,
    setInitialZoomLevel,
    setTriggerLeftDockClose,
    setMovingAutomatically,
  } = useAppState.getState();
  const { shaneClient } = useShaneClientContext();
  const {
    jumpToTopOfResults,
    parseKeywordInput,
    applyFilters,
    runSearch,
    isGeoJsonPoint,
    isGeoJsonWithCoordinates,
    searchResultsToGeoJson,
    setLocationPopUpForSearchBoxResult,
  } = useMapFunctions();
  const { openPopUp } = usePopUp({
    isSmallScreen,
    popup,
    popupContainerRef,
    mapContainer,
    map,
  });

  function capitalizeFirstWordOnly(str: string) {
    if (!str) return str; // Handle empty or null strings
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }

  const addLocationsToMap = useCallback(
    async (
      geojson: GeoJSON.FeatureCollection,
      skipFlyTo: boolean = false,
      skipAddingToMap?: boolean,
      skipAddingMarkerListeners?: boolean
    ): Promise<CustomMarker[] | undefined> => {
      const { markersRef, setMarkersRef } = useAppState.getState();

      writeToLog([
        "[UseSearch] running addLocationsToMap",
        skipFlyTo,
        loadingMore,
        markersRef.current,
        map.current,
        zoom,
        skipAddingToMap,
      ]);
      writeToLog(["[UseSearch] [addLocationsToMap] geojson", geojson]);

      // remove all current markers from map
      if ((markersRef?.current ?? []).length > 0 && !skipAddingToMap) {
        for (const m of markersRef.current) {
          (m.marker as Marker).remove();
        }
        writeToLog("[UseSearch] [addLocationsToMap] removed markers from map");
      }
      if (!map.current) return;

      if (newMarker) {
        newMarker.remove();
        setNewMarker(null);
      }

      let markersTemp: CustomMarker[] = [];
      let uniqueArray: CustomMarker[] = [];

      const source = map.current.getSource("markers") as GeoJSONSource;
      if (source) {
        source.setData(geojson);
      }

      const bounds = new mapboxgl.LngLatBounds();

      const processFeature = (feature: GeoJSON.Feature) => {
        const el = document.createElement("div");

        const source = feature?.properties?.source;
        const customIcon = feature?.properties?.customIcon;

        let markerClass: any | null = null;

        if (source) {
          const m = externalSources.find((a: any) => a.key === source)?.marker;
          markerClass = m ? { className: m } : null;
        } else if (customIcon) {
          markerClass = { className: "marker-emoji" };
        } else if (feature?.properties?.isPoi) {
          markerClass = { className: "marker-poi" };
        } else {
          markerClass =
            feature?.properties?.category === refugeRestroomsCategory
              ? { className: "marker-rr" }
              : markerMapping.find(
                  (m) =>
                    m.isCertified ===
                      (!feature?.properties?.isShaneLocation
                        ? false
                        : feature?.properties?.isCertified) &&
                    m.isRatingMode === showRatingMarkers &&
                    m.isShaneLocation ===
                      (feature?.properties?.isShaneLocation ?? false)
                );
        }
        if (markerClass) el.className = markerClass.className;

        const { searchSet, filterSet } = useAppState.getState();

        if (skipAddingToMap) {
        } else if (
          !allCertified &&
          searchSet?.what !== refugeRestroomsCategory &&
          !filterSet?.selectedFeatures.some(
            (item) =>
              item.toLowerCase() === refugeRestroomsCategory.toLowerCase()
          )
        ) {
          el.classList.add("marker-hidden");
        }

        if (showRatingMarkers && !source && !customIcon) {
          let ratingNumber = !feature?.properties?.isShaneLocation
            ? 0
            : feature?.properties?.ratingLikeMe
            ? Math.round(feature?.properties?.ratingLikeMe * 2) / 2
            : feature?.properties?.ratingAll
            ? Math.round(feature?.properties?.ratingAll * 2) / 2
            : Math.max(
                0,
                Math.min(
                  Math.round(
                    (feature?.properties?.ratings ?? []).find(
                      (r: any) =>
                        r.who ===
                        (profile?.gender ||
                          profile?.orientation ||
                          profile?.additionalGender)
                          ? "Similar identities"
                          : "All perspectives"
                      //TODO add fallback if there's nothing similar to you
                    )?.rating * 2
                  ) / 2,
                  5
                )
              );

          let rating = Number.isNaN(ratingNumber)
            ? "..."
            : ratingNumber === 0
            ? "..."
            : ratingNumber.toString();
          if (rating.length === 1) rating += ".0";

          const ratingElement = document.createElement("div");

          el.appendChild(ratingElement);
          el.style.display = "flex";
          el.style.justifyContent = "flex-start";
          el.style.paddingTop = feature?.properties?.isCertified
            ? "3px"
            : "5px";

          ratingElement.textContent = rating;
          ratingElement.style.width = "35px";
          ratingElement.style.display = "flex";
          ratingElement.style.justifyContent = "center";
          ratingElement.style.paddingTop = feature?.properties?.isCertified
            ? "8px"
            : "1px";
          ratingElement.style.marginLeft = feature?.properties?.isCertified
            ? "0px"
            : !feature?.properties?.isShaneLocation
            ? "-3px"
            : "1px";
          ratingElement.style.color = !feature?.properties?.isShaneLocation
            ? primary1
            : white;
          ratingElement.style.marginTop = !feature?.properties?.isShaneLocation
            ? "-4px"
            : "0px";

          el.classList.add("rating-marker");
        }

        if (customIcon && !source) {
          const ratingElement = document.createElement("div");

          el.appendChild(ratingElement);
          ratingElement.textContent = emoji.getUnicode(customIcon);
          ratingElement.style.fontSize = "14px";
          ratingElement.style.height = "25px";
          ratingElement.style.width = "25px";
          ratingElement.style.display = "flex";
          ratingElement.style.justifyContent = "center";
          ratingElement.style.alignItems = "center";
        }

        if (!isGeoJsonWithCoordinates(feature.geometry)) return;
        if (!isGeoJsonPoint(feature.geometry)) return;

        //@ts-ignore
        if (feature.geometry.coordinates.includes(undefined)) return;

        const marker = new mapboxgl.Marker(el).setLngLat(
          feature.geometry.coordinates
        );

        if (!skipAddingToMap) {
          marker.addTo(map.current!);
        }

        markersTemp.push({
          id: feature?.properties?.id,
          marker,
          priority:
            feature?.properties?.category === refugeRestroomsCategory
              ? "superhigh"
              : feature?.properties?.priority, //override to ensure all RR locations are visible
          show: false,
        });

        //SECTION MARKER LISTENERS
        const onMarkerClick = (e: any) => {
          e.stopPropagation();
          if (map.current && popupContainerRef.current) {
            setSelectedFeature(feature);

            if (!isGeoJsonWithCoordinates(feature.geometry)) return;
            if (!isGeoJsonPoint(feature.geometry)) return;

            setTimeout(() => {
              if (!popupContainerRef?.current) return;
              popup.setDOMContent(popupContainerRef.current);
              //@ts-ignore
              popup.setLngLat(feature.geometry.coordinates).addTo(map.current!);
              marker.setPopup(popup);

              if (resultsPosition === "collapsed" && isSmallScreen) {
                setResultsPosition("middle");
              }
              if (isSmallScreen) {
                setIsLocationDialogOpen(true);
                setDrawerContentLoaded(true);
              }

              if (!popup.isOpen()) {
                marker.togglePopup();
              }
            }, 100);
          }
        };
        const onMarkerMouseEnter = (e: any) => {
          // check that this marker isn't selectedFeature
          const { selectedFeature } = useAppState.getState();
          if (
            (feature as any)?.properties?.name ===
            selectedFeature?.properties?.name
          )
            return;

          if (map.current && popupContainerRef.current) {
            e.stopPropagation();

            if (!isGeoJsonWithCoordinates(feature.geometry)) return;
            if (!isGeoJsonPoint(feature.geometry)) return;

            namePopup
              .setLngLat(feature.geometry.coordinates)
              .addClassName("custom-popup")
              .addTo(map.current!);
            namePopup.setHTML(
              "<h4 style={{marginTop : '7px'}}>" +
                (feature?.properties?.name ?? "") +
                "</h4>"
            );

            marker.setPopup(namePopup);

            if (!namePopup.isOpen()) marker.togglePopup();
          }
        };
        const onMarkerMouseLeave = () => {
          // remove popup
          namePopup.remove();
        };

        if (!skipAddingMarkerListeners) {
          el.addEventListener("click", onMarkerClick);
          el.addEventListener("mouseenter", onMarkerMouseEnter);
          el.addEventListener("mouseleave", onMarkerMouseLeave);

          const cleanupListeners = () => {
            el.removeEventListener("click", onMarkerClick);
            el.removeEventListener("mouseenter", onMarkerMouseEnter);
            el.removeEventListener("mouseleave", onMarkerMouseLeave);
          };

          marker.on("remove", () => {
            cleanupListeners();
          });
        }
        //!SECTION

        bounds.extend(feature.geometry.coordinates);
      };

      writeToLog([
        "[UseSearch] [addLocationsToMap] processing each search result. Length:",
        geojson.features.length,
      ]);

      geojson.features.forEach((feature) => {
        processFeature(feature);
      });

      uniqueArray = markersTemp.filter(
        (item, index, self) => index === self.findIndex((t) => t.id === item.id)
      );

      setMarkersRef(uniqueArray);

      const { searchSet } = useAppState.getState();

      if (
        (!loadingMore && !skipFlyTo) ||
        (searchSet.what === "Shane Certified" && searchSet.where === "Anywhere")
      ) {
        const currentBounds = map.current.getBounds();

        if (currentBounds) {
          if (
            searchSet.where === "Anywhere" &&
            searchSet.what === "Shane Certified"
          ) {
            //fly to center at zoom level 2.5
            map.current.once("idle", () => {
              if (!map?.current) return;
              map.current.resize(); // Ensures map dimensions are correct
              map.current.jumpTo({ center: [-2.1036, 53.2947], zoom: 2.5 }); // Ensures position update
              map.current.flyTo({
                center: [-2.1036, 53.2947],
                zoom: 2.5,
                speed: 3,
                essential: true,
              });
            });
          } else if (!areBoundsEqual(currentBounds, bounds)) {
            // const z = map.current.getZoom();

            map.current.once("idle", () => {
              if (!map?.current) return;

              map.current.fitBounds(bounds, {
                padding: 50,
                //searchSet.where?.startsWith('{') ? 10 :

                maxZoom: 13,
                // z === 7
                //   ? 15
                //   : z < 5
                //   ? 11
                //   : searchSet.where === "Anywhere"
                //   ? 11
                //   : z,
                //searchSet.where?.startsWith('{') ? 10 :
                minZoom: 7, //z < 5 ? 5 : searchSet.where === "Anywhere" ? 11 : z,
              });
            });
          } else if (!skipAddingToMap) {
            map.current.panTo(map.current.getCenter());
          }
        }

        const handleMoveEnd = () => {
          if (!map.current) return;
          setIsSearching(false);
          // map.current.resize();
          // map.current.panTo(map.current.getCenter());

          map.current.off("moveend", handleMoveEnd);
        };

        map.current.on("moveend", handleMoveEnd);
      } else {
        // force handlemapend to run
        if (!skipAddingToMap) {
          map.current.panTo(map.current.getCenter());
        }

        setIsSearching(false);
      }

      // Initialize LngLatBounds
      {
        const bounds1 = new mapboxgl.LngLatBounds();

        // Extend bounds with each marker's coordinates
        uniqueArray.forEach((marker) => {
          const lngLat = marker.marker.getLngLat(); // Get the marker's coordinates
          bounds1.extend([lngLat.lng, lngLat.lat]);
        });

        setInitialSearchViewportBounds(bounds1);
      }

      return uniqueArray;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      map,
      showRatingMarkers,
      setSelectedFeature,
      popup,
      popupContainerRef,
      loadingMore,
      profile,
      isSmallScreen,
      resultsPosition,
    ]
  );

  const onSearchDataChanged = async (newData: SearchSet) => {
    const { filterSet, searchSet } = useAppState.getState();
    // if (!isEqual(newData, searchSet))
    {
      writeToLog([
        "[UseSearch] running onsearchdatachanged",
        newData,
        searchSet,
      ]);

      if (newData?.what === refugeRestroomsCategory) {
        newData.whatSub = "";
      }

      setSearchSet(newData);

      const newSearchVal = filterSet.selectedFeatures
        .map((c: string) => {
          return c.replaceAll("*", "");
        })
        .join(", ");

      //set fake searchbox value
      if (newData?.keywordSearchBoxSelection) {
        setFakeSearchInputValue(
          newSearchVal.length === 0
            ? newData.keywordSearchBoxSelection?.properties?.name ??
                (newData.keywordSearchBoxSelection as any)?.name
            : (newData.keywordSearchBoxSelection?.properties?.name ??
                (newData.keywordSearchBoxSelection as any)?.name) +
                " (" +
                newSearchVal +
                ")"
        );
      } else if (newData?.keyword) {
        setFakeSearchInputValue(
          newSearchVal.length === 0
            ? newData.keyword
            : newData.keyword + " (" + newSearchVal + ")"
        );
      } else {
        let value =
          newData.whatSub === "Search all"
            ? newData.what
            : (newData?.whatSub ?? "").length === 0
            ? newData.what
            : newData.whatSub;

        value = capitalizeFirstWordOnly(value ?? "");

        if (
          ["Online only", "Anywhere", "Near me", "Somewhere specific"].includes(
            newData?.where ?? ""
          ) &&
          newData?.where
        ) {
          value += " " + newData?.where.toLowerCase();
        } else if (newData?.where === "This area") {
          value += " in " + newData?.where.toLowerCase();
        } else {
          value +=
            " in " + JSON.parse(newData?.where ?? "{}")?.properties?.name;
        }

        value = value.trim();

        if (value === "anywhere") value = "Anything, anywhere";

        setFakeSearchInputValue(
          newSearchVal.length === 0 ? value : value + " (" + newSearchVal + ")"
        );
      }

      handleSearchBoxChange(newData);
    }
  };

  const debouncedOnSearchDataChanged = debounce(onSearchDataChanged, 2000, {
    leading: true,
    trailing: false,
  });

  // //$ get geojson for searchresults and add locations to map
  const updateMapWithSearchResults = useCallback(
    async (skipFlyTo?: boolean, forceMove?: boolean) => {
      const {
        newMarker,
        searchResults,
        searchSet,
        hideMap,
        resultsPageNumber,
        isSmallScreen,
        lastSearchType,
        markersRef,
        setMarkersRef,
      } = useAppState.getState();
      const markers = markersRef.current;

      writeToLog([
        "[UseSearch] running updateMapWithSearchResults",
        skipFlyTo,
        forceMove,
        searchResults,
      ]);
      if (!popupContainerRef.current) {
        const container = document.createElement("div");
        popupContainerRef.current = container;
        popupContainerRef.current.setAttribute("id", "popup-container");
        popupContainerRef.current.style.width = "fit-content";
        popupContainerRef.current.style.paddingTop = "6px";
        popupContainerRef.current.style.paddingBottom = "6px";
        document.body.appendChild(popupContainerRef.current);
      }

      if (newMarker) {
        newMarker.remove();
        setNewMarker(null);
      }

      if (searchResults && searchResults.length > 0) {
        writeToLog([
          "[UseSearch] running updateMapWithSearchResults",
          searchResults,
          lastSearchType,
          resultsPageNumber,
        ]);

        setSearchSet({
          ...searchSet,
          keywordSearchBoxSelection: null,
        });

        if (!hideMap) {
          //@ts-ignore
          const geoJson = searchResultsToGeoJson(searchResults);

          await addLocationsToMap(
            geoJson,
            resultsPageNumber > 2 || lastSearchType === "zoom" || skipFlyTo
          );

          if (resultsPageNumber > 2 || lastSearchType === "zoom" || forceMove) {
            if (map?.current) {
              map.current.setCenter(map.current.getCenter()); // Triggers 'move'
            }
          }
        } else {
          if (isSmallScreen) {
            setTimeout(() => {
              setResultsPosition("expanded");
            }, 250);
          } else {
            setSelectedFeature(
              //@ts-ignore
              searchResultsToGeoJson([searchResults[0]]).features[0]
            );
            setTimeout(() => {
              setIsLocationDialogOpen(true);
            }, 500);
          }
          setIsSearching(false);
        }
        setLoadingMore(false);
      } else if (!selectedFeature) {
        if (markers) {
          for (const m of markers) {
            (m.marker as Marker).remove();
          }
          setMarkersRef([]);
          setIsSearching(false);
        }
      }
    },
    [
      addLocationsToMap,
      map,
      popupContainerRef,
      searchResultsToGeoJson,
      selectedFeature,
      setIsLocationDialogOpen,
      setIsSearching,
      setLoadingMore,
      setNewMarker,
      setResultsPosition,
      setSearchSet,
      setSelectedFeature,
    ]
  );

  const onSearch = useCallback(
    async (type: "category" | "keyword", newData: SearchSet) => {
      const { filterSet, markersRef } = useAppState.getState();

      await debouncedOnSearchDataChanged(newData);
      writeToLog(["[UseSearch] running onSearch", type, newData]);
      setLastSearchType(undefined);
      //hide location dialog and location drawer
      setIsLocationDialogOpen(false);
      setSearchSet({ ...newData });
      setLastSearchType(undefined);
      setInitialZoomLevel(map.current?.getZoom() ?? null);

      setResultsPageNumber(1);
      if (type === "keyword") {
        const { isRunningInWebView } = useAppState.getState();

        writeToGA4(
          isRunningInWebView ? "mobile_app" : "web_app",
          "keyword_search",
          { keyword: newData?.keyword ?? "None" }
        );
      } else {
        const { isRunningInWebView } = useAppState.getState();

        writeToGA4(
          isRunningInWebView ? "mobile_app" : "web_app",
          "category_search",
          {
            what: newData?.what,
            whatSub: newData?.whatSub ?? "N/A",
            where: newData?.where?.startsWith("{")
              ? JSON.parse(newData?.where)?.properties?.name ?? ""
              : newData?.where,
            poiCategories:
              (newData?.poiCategories ?? []).length === 0
                ? "None"
                : JSON.stringify(
                    (newData?.poiCategories ?? []).map((pc: any) => {
                      return pc.poiCategoryName;
                    })
                  ),
          }
        );
      }

      if (type === "keyword" && newData?.keyword) {
        if (newData?.keywordSearchBoxSelection) {
          // non-poi search from suggestion

          newData.keyword = (newData?.keywordSearchBoxSelection as any)?.name;
          newData.keywordSearchBoxSelection = null;
        } else {
          const terms = await parseKeywordInput(newData?.keyword);

          if (terms) {
            writeToLog(["[UseSearch] [onSearch] terms", JSON.stringify(terms)]);
            const locations = terms.filter((t: any) => t?.location);

            if ((locations ?? []).length > 0) {
              newData.where = JSON.stringify({
                properties: { ...locations[0].location },
              });
            }

            if (terms.length === 1 && locations.length === 1) {
              // change to an all shane-rated places search against that country
              type = "category";
              newData.what = "All Shane-Rated Places";
            }

            const categories = terms.filter((t: any) => t?.poiCategory);
            const words = terms.filter(
              (t: any) => !t?.poiCategory && !t?.location
            );

            try {
              if (categories.length === 1 && words.length === 0) {
                const { data } = await fetchCategoryForPoiCat({
                  client: shaneClient,
                  path: {
                    poiCatName: categories[0].poiCategory?.displayName,
                  },
                });

                if (data) {
                  type = "category";
                  newData.what = data[0].categoryName;
                }
              }
            } catch {}

            newData.keyword = terms
              .filter(
                (
                  t: any //!t?.poiCategory &&
                ) => !t?.location
              )
              .map((t: any) => {
                return t.word;
              })
              .join(" ");
          }
        }
      }

      setSearchSet(newData);

      if (type === "category" && newData?.where === mapHiddenWhereCategory) {
        setHideMap(true);
      } else {
        setHideMap(false);
      }

      //clean up old search results from map
      if (markersRef.current) {
        for (const m of markersRef.current) {
          (m.marker as Marker).remove();
          // setMarkers([]);
        }
      }
      setSelectedFeature(null);

      await runSearch(
        (b: boolean) => setIsSearching(b),
        //@ts-ignore
        setSearchResults,
        setUnfilteredSearchResults,
        1,
        newData,
        filterSet,
        () => setResultsPageNumber(2),
        profile?.gender ?? null,
        profile?.additionalGender ?? null,
        profile?.orientation ?? null,
        (b: boolean) => setHasMoreResults(b),
        type,
        map.current,
        [],
        [],
        isSmallScreen,
        setIsNoResultsModalOpen
      );

      const { searchSet: ss } = useAppState.getState();

      updateMapWithSearchResults(ss.where === "This area" ? true : false, true);

      // if newData.where.startsWith('{'), fly to middle of location
      if ((newData?.where ?? "").startsWith("{")) {
        const loc = JSON.parse(newData?.where ?? "{}");

        if (!map?.current) return;

        if (loc?.properties?.bbox) {
          map.current.fitBounds(loc?.properties?.bbox, {
            padding: { top: 10, bottom: 25, left: 15, right: 5 },
            speed: 0.6,
          });
        } else {
          //center
          map.current.flyTo({
            center: loc?.geometry?.coordinates,
            zoom: 11,
            speed: 0.6,
          });
        }
      }

      // if toilets, fit bounds
      if (newData?.what === refugeRestroomsCategory && map.current) {
        const { initialRRLngLat } = useAppState.getState();

        if (initialRRLngLat) {
          setMovingAutomatically(true);
          map.current.flyTo({
            center: initialRRLngLat,
            zoom: 11,
            speed: 0.6,
          });

          const handleMoveEnd = () => {
            if (!map.current) return;
            setTimeout(() => {
              setMovingAutomatically(false);
            }, 400);
            map.current.off("moveend", handleMoveEnd);
          };

          map.current.on("moveend", handleMoveEnd);
        }
      }

      const { isNoResultsModalOpen } = useAppState.getState();

      if (isSmallScreen && !isNoResultsModalOpen) {
        setResultsPosition("middle");
        setDrawerContentLoaded(true);
      }
      setIsSearchDialogOpen(false);

      //* ensure search-this-area doesn't pop up immediately after search
      setTimeout(() => {
        setLastSearchType("initial");
      }, 800);
    },
    [
      debouncedOnSearchDataChanged,
      setSearchSet,
      setLastSearchType,
      setInitialZoomLevel,
      setResultsPageNumber,
      setHideMap,
      setSelectedFeature,
      runSearch,
      setIsSearching,
      setSearchResults,
      setUnfilteredSearchResults,
      profile,
      setHasMoreResults,
      map,
      isSmallScreen,
      setIsNoResultsModalOpen,
      updateMapWithSearchResults,
      setResultsPosition,
      setDrawerContentLoaded,
      setIsSearchDialogOpen,
      parseKeywordInput,
      setIsLocationDialogOpen,
      setMovingAutomatically,
      shaneClient,
    ]
  );

  const debouncedOnSearch = debounce(onSearch, 500, {
    leading: false,
    trailing: true,
  });

  const handleSearchBoxChange = useCallback(
    async (searchSet_: SearchSet) => {
      writeToLog([
        "[UseInteraction] [handleSearchBoxChange] change to searchbox value",
        searchSet_,
      ]);

      if (searchSet_?.keywordSearchBoxSelection) setIsSearching(true);

      const { isRunningInWebView, markersRef } = useAppState.getState();
      const markers = markersRef.current;

      writeToGA4(
        isRunningInWebView ? "mobile_app" : "web_app",
        "suggestion_select",
        {
          featureType:
            (searchSet_?.keywordSearchBoxSelection as any)?.feature_type ??
            "None",
          suggestionName:
            (searchSet_?.keywordSearchBoxSelection as any)?.name ?? "None",
        }
      );

      if (
        !searchSet_?.keywordSearchBoxSelection ||
        JSON.stringify(searchSet_?.keywordSearchBoxSelection) === "{}"
      )
        return;

      let runOpenPopUp = false;

      if (
        (searchSet_?.keywordSearchBoxSelection as any)?.feature_type === "brand"
      ) {
        searchSet_ = {
          ...searchSet_,
          keyword: (searchSet_?.keywordSearchBoxSelection as any)?.name,
          keywordSearchBoxSelection: null,
        };
        //* change to a keyword search
        setSearchSet(searchSet_);
        runOpenPopUp = false;
      } else if (
        (searchSet_?.keywordSearchBoxSelection as any)?.feature_type ===
        "category"
      ) {
        // get parent category from poicat
        const { data } = await fetchCategoryForPoiCat({
          client: shaneClient,
          path: {
            poiCatName: (searchSet_?.keywordSearchBoxSelection as any)
              ?.poi_category[0],
          },
        });

        if (data) {
          searchSet_ = {
            ...searchSet_,
            keyword: (searchSet_?.keywordSearchBoxSelection as any).name,
            keywordSearchBoxSelection: null,
            what: data[0].categoryName,
            poiCategories: [
              {
                canonicalIds: [
                  (searchSet_?.keywordSearchBoxSelection as any)
                    ?.poi_category_ids,
                ],
              },
            ],
            where: "This area",
          };
          setSearchSet(searchSet_);
          runOpenPopUp = false;
        }
      }
      // ensure selection is in our country allow list
      else {
        if (
          !countryCodeAllowList.includes(
            //@ts-ignore
            searchSet_?.keywordSearchBoxSelection?.properties?.context.country
              .country_code //FIXME
          )
        ) {
          //* pop a toast
          let placeType = "";
          try {
            placeType =
              //@ts-ignore
              searchSet_?.keywordSearchBoxSelection?.properties?.feature_type ??
              "place"; //FIXME
          } catch (e) {
            placeType = "place";
          }

          let message = searchBoxSuggestionClickBlockedError.replace(
            "###PLACE_TYPE###",
            placeType
          );

          toast.error(<div dangerouslySetInnerHTML={{ __html: message }} />, {
            hideProgressBar: true,
            transition: Slide,
            position: isSmallScreen ? "top-center" : "top-right",
            pauseOnHover: true,
            autoClose: 7000,
          });

          return;
        }

        if (
          (markers ?? []).length === 0 ||
          (markers ?? []).length > 1 ||
          ((markers ?? []).length === 1 &&
            selectedFeature?.properties?.name !==
              //@ts-ignore
              searchSet_?.keywordSearchBoxSelection?.properties?.name) //FIXME
        ) {
          if ((markers ?? []).length === 1) {
            //remove current marker
            if (markers) (markers[0].marker as Marker).remove();
          }
          runOpenPopUp = true;
        }
      }

      if (
        searchSet_?.keywordSearchBoxSelection?.properties?.feature_type ===
        "place"
      ) {
        // need to handle non-pois differently so we can do a search
        searchSet_ = {
          ...searchSet_,
          keyword: null,
          keywordSearchBoxSelection: null,
          what: "All Shane-Rated Places",
          where: JSON.stringify(searchSet_?.keywordSearchBoxSelection),
        };

        setSearchSet(searchSet_);
        runOpenPopUp = false;
      } else if (
        (searchSet_?.keywordSearchBoxSelection as any)?.properties
          ?.feature_type !== "poi" &&
        !!searchSet_?.keywordSearchBoxSelection
      ) {
        // need to handle non-pois differently so we can do a search
        searchSet_ = {
          ...searchSet_,
          //@ts-ignore
          keyword:
            searchSet_.keyword ??
            searchSet_?.keywordSearchBoxSelection?.properties?.name ??
            (searchSet_?.keywordSearchBoxSelection as any)?.name, //FIXME
          keywordSearchBoxSelection: null,
        };

        setSearchSet(searchSet_);
        runOpenPopUp = false;
      }

      if (runOpenPopUp && map.current) {
        await setLocationPopUpForSearchBoxResult(
          //@ts-ignore
          searchSet_,
          map.current,
          popupContainerRef,
          addLocationsToMap,
          openPopUp,
          isSmallScreen,
          () => setIsSearchDialogOpen(false),
          profile?.genderId ?? null,
          profile?.orientationId ?? null,
          profile?.additionalGenderId ?? null
        );

        const { selectedFeature: sf } = useAppState.getState();

        if (sf?.properties?.isShaneLocation || isSmallScreen)
          setIsLocationDialogOpen(true);

        setTriggerLeftDockClose(true);
        setTimeout(() => {
          setTriggerLeftDockClose(false);
        }, 1000);
      } else {
        //* do a search
        const tempSearchSet = {
          ...searchSet_,
          keyword: searchSet_?.what ? null : searchSet_.keyword,
        };

        await onSearch(
          tempSearchSet?.keyword ? "keyword" : "category",
          tempSearchSet
        );
      }
    },
    [
      isSmallScreen,
      profile,
      selectedFeature,
      map,
      popupContainerRef,
      addLocationsToMap,
      openPopUp,
      onSearch,
      setIsLocationDialogOpen,
      setIsSearchDialogOpen,
      setLocationPopUpForSearchBoxResult,
      setSearchSet,
      setTriggerLeftDockClose,
      shaneClient,
      setIsSearching,
    ]
  );

  //* changing filters / quicklinks and features
  const onFilterDataChanged = async (newData: FilterSet) => {
    const {
      searchResults,
      unfilteredSearchResults,
      searchSet,
      filterSet: originalFilterSet,
    } = useAppState.getState();

    if (JSON.stringify(newData) === JSON.stringify(originalFilterSet)) return;

    setFilterSet({ ...newData });

    writeToLog([
      "[UseSearch] running onFilterDataChanged",
      newData,
      unfilteredSearchResults,
      searchSet,
    ]);

    if (
      (searchResults ?? []).length === 0 &&
      (newData?.selectedFeatures ?? []).length === 0 &&
      !newData?.minimumRating &&
      (unfilteredSearchResults ?? []).length === 0
    ) {
      writeToLog("[UseSearch] [onFilterDataChanged] not applying filters");
      setFakeSearchInputValue("");
      setIsSearchDialogOpen(true);
      return;
    }

    const { isRunningInWebView } = useAppState.getState();

    writeToGA4(isRunningInWebView ? "mobile_app" : "web_app", "filters_apply", {
      selectedFilters: newData?.selectedFilters,
      selectedFeatures: newData?.selectedFeatures,
    });

    if (
      (searchResults ?? []).length === 0 &&
      (unfilteredSearchResults ?? []).length > 0 &&
      (newData?.selectedFeatures ?? []).length === 0 &&
      !newData?.minimumRating
    ) {
      writeToLog([
        "[UseSearch] [onFilterDataChanged] clearing filters",
        unfilteredSearchResults,
        searchSet,
      ]);
      const { isRunningInWebView } = useAppState.getState();

      writeToGA4(
        isRunningInWebView ? "mobile_app" : "web_app",
        "filters_clear"
      );
      if (searchSet.isQuicklinkSearch) {
        setSearchResults([]);
        setUnfilteredSearchResults([]);
      } else {
        setSearchResults(unfilteredSearchResults);
      }
      setTimeout(() => {
        updateMapWithSearchResults(false, true);
      }, 1000);

      return;
    }

    if (
      (newData?.selectedFeatures ?? []).length === 0 &&
      !newData?.minimumRating &&
      searchSet?.isQuicklinkSearch
    ) {
      writeToLog(
        "[UseSearch] [onFilterDataChanged] quicklink search - clearing filters"
      );
      setFakeSearchInputValue("");
      setIsSearchDialogOpen(true);
      setSearchResults([]);
      setTimeout(() => {
        //@ts-ignore
        setSearchSet({ ...searchSet, isQuicklinkSearch: false }); //FIXME
        setUnfilteredSearchResults([]);

        updateMapWithSearchResults(true, true);
      }, 1000);
      return;
    }
    const newSearchVal = newData.selectedFeatures
      .map((c: string) => {
        return c.replaceAll("*", "");
      })
      .join(", ");

    const searchString =
      (fakeSearchInputValue ?? "").substring(
        0,
        fakeSearchInputValue.lastIndexOf(" (") ?? fakeSearchInputValue.length
      ) ?? fakeSearchInputValue;

    setFakeSearchInputValue(
      (
        (searchString.length === 0 ? fakeSearchInputValue : searchString) +
        " (" +
        newSearchVal +
        ")"
      ).trim()
    );

    if ((unfilteredSearchResults ?? []).length === 0) {
      //No search results, so we should be searching, not filtering
      let tempSearchSet = { what: "", where: "This area" };

      if (newData.selectedFeatures.includes("Shane certified")) {
        tempSearchSet.what = "Shane Certified";
      } else if (
        newData.selectedFeatures.includes("Gender-inclusive toilets")
      ) {
        tempSearchSet.what = "Gender-Inclusive Toilets";
      } else {
        tempSearchSet.what = "All Shane-Rated Places";
      }

      //@ts-ignore
      tempSearchSet = { ...tempSearchSet, isQuicklinkSearch: true };
      setSearchSet({ ...tempSearchSet, isQuicklinkSearch: true });

      await runSearch(
        (b: boolean) => setIsSearching(b),
        //@ts-ignore
        setSearchResults, //FIXME
        setUnfilteredSearchResults,
        1,
        tempSearchSet,
        { ...newData },
        () => setResultsPageNumber(resultsPageNumber + 1),
        profile?.gender ?? null,
        profile?.additionalGender ?? null,
        profile?.orientation ?? null,
        (b: boolean) => setHasMoreResults(b),
        "category",
        map.current,
        [],
        [],
        isSmallScreen,
        setIsNoResultsModalOpen
      );

      updateMapWithSearchResults(false, true);

      setIsSearchDialogOpen(false);

      // if toilets, fit bounds
      if (
        newData?.selectedFeatures.some(
          (item) => item.toLowerCase() === refugeRestroomsCategory.toLowerCase()
        ) &&
        map.current
      ) {
        const { initialRRLngLat } = useAppState.getState();

        if (initialRRLngLat) {
          setMovingAutomatically(true);
          map.current.flyTo({
            center: initialRRLngLat,
            zoom: 11,
            speed: 0.6,
          });
          const handleMoveEnd = () => {
            if (!map.current) return;
            setTimeout(() => {
              setMovingAutomatically(false);
            }, 400);
            map.current.off("moveend", handleMoveEnd);
          };

          map.current.on("moveend", handleMoveEnd);
        }
      }

      setTimeout(() => {
        setLastSearchType("initial");
      }, 800);

      return;
    }

    await applyFilters(
      //@ts-ignore
      unfilteredSearchResults, //FIXME
      newData,
      (b: boolean) => setIsSearching(b),
      setSearchResults,
      isSmallScreen
    );

    setTimeout(() => {
      // remove markers which are no longer in searchresults
      updateMapWithSearchResults(true, true);
      jumpToTopOfResults();
    }, 400);

    // if toilets, fit bounds
    if (
      newData?.selectedFeatures.some(
        (item) => item.toLowerCase() === refugeRestroomsCategory.toLowerCase()
      ) &&
      map.current
    ) {
      const { initialRRLngLat } = useAppState.getState();

      if (initialRRLngLat) {
        setMovingAutomatically(true);
        map.current.flyTo({
          center: initialRRLngLat,
          zoom: 11,
          speed: 0.6,
        });
        const handleMoveEnd = () => {
          if (!map.current) return;
          setTimeout(() => {
            setMovingAutomatically(false);
          }, 400);
          map.current.off("moveend", handleMoveEnd);
        };

        map.current.on("moveend", handleMoveEnd);
      }
    }
  };

  const getPoiCategories = async (categoryName: string, newData: any) => {
    writeToLog(["[UseSearch] running getPoiCategories", categoryName, newData]);
    // get poicategories
    if (categoryName) {
      let query: any = {
        categoryName,
        version: 1,
      };

      if (categoryName !== "Other") {
        query.parentCategoryName = newData.what ?? "";
      }

      // eslint-disable-next-line
      const { data } = await listPoiCategoriesByCategory({
        client: shaneClient,
        query,
      });

      //TODO handle error

      if (data && data.length >= 1) {
        const poi = data
          .filter((d: any) => d?.poiCategoryName)
          .sort(
            (a: any, b: any) => a.order - b.order || a.innerOrder - b.innerOrder
          );

        let poiGroups: {
          poiCategoryName: string;
          uuids: [string];
          canonicalIds: [string];
        }[] = [];
        poi.forEach((poiItem: any) => {
          let group = poiGroups.find(
            (item) =>
              item.poiCategoryName === (poiItem.poiCategoryName as string)
          );

          if (group) {
            // group already exists, add uuid and canonicalId
            group.uuids.push(poiItem?.uuid as string);
            group.canonicalIds.push(poiItem?.canonicalId as string);
          } else {
            // else group does not exist, add name and uuid item
            if (poiItem?.poiCategoryName && poiItem?.uuid) {
              poiGroups.push({
                poiCategoryName: poiItem?.poiCategoryName,
                uuids: [poiItem?.uuid],
                canonicalIds: [poiItem?.canonicalId as string],
              });
            }
          }
        });

        return poiGroups.length > 0 ? poiGroups : undefined;
      } else {
        return undefined;
      }
    } else {
      return [];
    }
  };

  const onReviewVote = useCallback(
    async (reviewId: number, direction: "up" | "down") => {
      const { error } = await voteOnReview({
        path: {
          reviewId,
        },
        query: {
          direction,
        },
      });

      if (error) {
        toast.success("Couldn't vote on review, please try again later", {
          hideProgressBar: true,
          transition: Slide,
          position: isSmallScreen ? "top-center" : "top-right",
        });
      }
    },
    [isSmallScreen] // Dependency array
  );

  const stableSelectedFeature = useMemo(
    () => selectedFeature,
    [selectedFeature]
  );

  const fetchLocationData = useCallback(async () => {
    const selectedFeature = stableSelectedFeature;
    if (
      !selectedFeature?.properties?.id ||
      selectedFeature?.properties?.id === databaseLocation?.locationId ||
      selectedFeature?.properties?.name === databaseLocation?.name
    )
      return;

    writeToLog([
      "[UseSearch] running fetchLocationData",
      selectedFeature?.properties,
      databaseLocation,
    ]);

    if (
      selectedFeature?.properties?.id.toString() ===
        selectedFeature?.properties?.mapboxId ||
      (typeof selectedFeature?.properties?.id === "number" &&
        selectedFeature?.properties?.id < 0)
    ) {
      //**NOT ON SHANE */
      //* reshape selectedFeature to look like databaseLocation, set and open drawer
      let tempLocation: any = {
        category: null,
        categoryId: null,
        certificationExpiryDate: null,
        description: null,
        isCertified: 0,
        isClaimed: 0,
        isPromoted: 0,
        lat: selectedFeature.geometry[1],
        lng: selectedFeature.geometry[0],
        locationId: null,
        mapboxId: selectedFeature?.properties?.mapboxId,
        name: selectedFeature?.properties?.name,
        ratingsBreakdown: null,
        reviews: null,
        tags: selectedFeature?.properties?.tags,
      };
      setDatabaseLocation(tempLocation);
      setDrawerContentLoaded(true);
      return;
    }

    try {
      const { data, error } = await fetchLocation({
        client: shaneClient,
        path: { locationId: selectedFeature?.properties?.id.toString() },
      });

      if (
        error &&
        ((error as any)?.error_message ?? "") !== "No records found"
      ) {
        throw Error("Couldn't load location data, please try again later");
      }

      writeToLog([
        "[UseSearch] [fetchLocationData] fetched location",
        data,
        selectedFeature,
      ]);

      let tempLocation: any = {};

      if (data) {
        tempLocation = data;

        if (runInDummyDataMode) {
          tempLocation.reviews = dummyReviews;
          tempLocation.ratingsBreakdown = [
            {
              type: "peopleLikeYou",
              ratings: [
                { label: "Overall Safety", rating: 5 },
                { label: "Community Vibe", rating: 5 },
                { label: "Inclusive Facilities", rating: 5 },
                { label: "Trans Inclusivity", rating: 5 },
                { label: "Visible Allyship", rating: 5 },
              ],
            },
            {
              type: "allPerspectives",
              ratings: [
                { label: "Overall Safety", rating: 5 },
                { label: "Community Vibe", rating: 5 },
                { label: "Inclusive Facilities", rating: 5 },
                { label: "Trans Inclusivity", rating: 5 },
                { label: "Visible Allyship", rating: 5 },
              ],
            },
          ] as unknown as RatingsBreakdown;
        } else {
          // Fetch ratings breakdown
          const { data: ratingsData, error } =
            await fetchLocationRatingSummaries({
              client: shaneClient,
              path: { locationId: selectedFeature?.properties?.id },
              query: {
                genderId:
                  typeof profile?.genderId === "string"
                    ? parseInt(profile?.genderId)
                    : profile?.genderId ?? undefined,
                orientationId:
                  typeof profile?.orientationId === "string"
                    ? parseInt(profile?.orientationId)
                    : profile?.orientationId ?? undefined,
                additionalGenderId:
                  typeof profile?.additionalGenderId === "string"
                    ? parseInt(profile?.additionalGenderId)
                    : profile?.additionalGenderId ?? undefined,
              },
            });

          if (error) {
            writeToLog([
              "[UseSearch] [fetchLocationData] Couldn't load ratings: ",
              error,
            ]);

            // return;
            // doesn't always need an error popup, usually recovers
          }

          if (ratingsData) {
            // Transform ratingsData into the desired format and set state
            let transformedData: any[] = [];
            const allRatings = ratingsData
              .filter(
                (r: any) => r.type === "everyone" && r.question !== "Overall"
              )
              .map((r: any) => {
                return { label: r.question, rating: r.averageResponse };
              });

            transformedData.push({
              type: "allPerspectives",
              ratings: allRatings,
            });

            if (
              profile?.genderId ||
              profile?.additionalGenderId ||
              profile?.orientationId
            ) {
              const plyRatings = ratingsData
                .filter(
                  (r: any) =>
                    r.type === "people like you" && r.question !== "Overall"
                )
                .map((r: any) => {
                  return { label: r.question, rating: r.averageResponse };
                });

              transformedData.push({
                type: "peopleLikeYou",
                ratings: plyRatings,
              });
            }

            // handle if any specific questions don't have a rating
            for (const t of transformedData) {
              if (t?.ratings.length > 0 && t?.ratings.length < 5) {
                //* fill in the gaps
                const allLabels = defaultResponses.ratings.map(
                  (r: any) => r.label
                );

                const ratedLabels = t?.ratings.map((r: any) => r.label);

                const missing = allLabels.filter((r: string) => {
                  return !ratedLabels.includes(r);
                });

                const final = t.ratings
                  .map((j: any) => {
                    return {
                      label: j.label,
                      rating:
                        typeof j.rating === "string"
                          ? parseInt(j.rating)
                          : j.rating,
                    };
                  })
                  .concat(
                    missing.map((label: string) => {
                      return { label, rating: 0 };
                    })
                  );

                t.ratings = final;
              }
            }
            tempLocation.ratingsBreakdown =
              transformedData as unknown as RatingsBreakdown;
          }

          // get reviews
          const { data: reviewsData, error: reviewError } =
            await getLocationReviews({
              client: shaneClient,
              path: {
                locationId: selectedFeature?.properties?.id,
              },
              query: {
                pageNumber: 1,
                pageSize: reviewsPageSize,
              },
            });

          if (reviewError) {
            throw Error("Couldn't load reviews");
          }

          if ((reviewsData as any)?.data?.reviews) {
            //transform reviews
            const transformedData = (
              (reviewsData as any)?.data?.reviews as any[]
            ).map((r) => {
              return {
                reviewId: r.reviewId,
                gender: r.gender,
                orientation: r.orientation,
                //? additionalGender??
                body: r.reviewBody,
                title: r.reviewTitle,
                usefulCount: r.voteCountDisplay,
                tags: JSON.parse(r.tags ?? "[]"),
                rating: r.overallRating,
                onReviewVote: onReviewVote,
              };
            });

            tempLocation.reviews = transformedData;
          }
        }

        setDatabaseLocation(tempLocation);
        setDrawerContentLoaded(true);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error((error as any).message, {
        hideProgressBar: true,
        transition: Slide,
        position: isSmallScreen ? "top-center" : "top-right",
      });
    }
  }, [
    shaneClient,
    profile?.genderId,
    profile?.orientationId,
    profile?.additionalGenderId,
    isSmallScreen,
    onReviewVote,
    databaseLocation,
    stableSelectedFeature,
    setDatabaseLocation,
    setDrawerContentLoaded,
  ]);

  return {
    onSearchDataChanged: debouncedOnSearchDataChanged,
    addLocationsToMap,
    onSearch: debouncedOnSearch,
    onFilterDataChanged,
    getPoiCategories,
    fetchLocationData,
    onReviewVote,
    updateMapWithSearchResults,
  };
}
